import { toCsvGrid } from '@/lib/csv'
import type { Program, Segment, SlateProgram, WbProgram } from '@/types'

/**
 * The translations table (ADR-0016 § 9), in the layout the S2C translation
 * sheets already use. Two independent lists side by side, one per link
 * direction (ADR-0016 § 2):
 *
 * - Columns A–B, WB program -> slate program: the form's dropdown values and
 *   the slate prompt each one writes, `wb_program.slate_program_id` (PC-37).
 * - Columns D–E, slate program -> WB program: every enabled slate program and
 *   the WB program it maps back to, `slate_program.wb_program_id` (ADR-0019 § 6).
 *
 * The two links are set independently and may disagree (ADR-0021).
 *
 * Names are reproduced exactly, trailing spaces included: "Nursing" and
 * "Nursing " are different values in Slate.
 *
 * Unmapped rows too, in both lists, with the other side blank (PC-27): a
 * blank is a mapping someone still has to set. Every WB program is listed,
 * whatever its crawled programs' status or page; only disabled slate programs
 * are left out.
 */

export const hasProgramPage = (p: Pick<Program, 'programUrl'>) => Boolean(p.programUrl?.trim())

/**
 * A program the client offers: one with a program page, or one a user added
 * by hand, which needs none (PC-31: quick adds like Pre-Med, and "Undecided").
 */
export const isOffered = (p: Pick<Program, 'programUrl' | 'source'>) => p.source === 'manual' || hasProgramPage(p)

/**
 * A graduate program: on the Graduate tab, under no WB program (PC-25,
 * ADR-0022). Certificate and unstated levels count as undergraduate.
 */
export const isGraduate = (p: Pick<Program, 'level'>) => p.level === 'graduate'

export interface ForwardRow {
  wbProgram: string
  /** The slate program written for this WB program; null when it has none. */
  slateName: string | null
}

export interface ReverseRow {
  slateName: string
  /** Null when the slate program is mapped to no WB program. */
  wbProgram: string | null
}

const byText = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: 'base' })

/**
 * One row per WB program, the dropdown value, with its own slate program
 * (PC-37). A disabled one counts as none: Slate no longer offers it
 * (ADR-0016 § 6).
 */
export function forwardRows(wbPrograms: WbProgram[]): ForwardRow[] {
  return wbPrograms
    .map((w) => ({ wbProgram: w.name, slateName: w.slateProgram?.enabled ? w.slateProgram.slateName : null }))
    .sort((a, b) => byText(a.wbProgram, b.wbProgram))
}

/** Every enabled slate program and its WB program: disabled ones are not in the client's Slate list any more. */
export function reverseRows(slatePrograms: SlateProgram[]): ReverseRow[] {
  return slatePrograms
    .filter((s) => s.enabled)
    .map((s) => ({ slateName: s.slateName, wbProgram: s.wbProgram?.name ?? null }))
    .sort((a, b) => byText(a.slateName, b.slateName))
}

const INTRO_FORWARD =
  "This list of programs that reflects what's currently offered at your institution, and will be displayed in the Search Form dropdown. This program list is comprehensive of all areas of study (including majors, minors, and concentrations when appropriate with the addition of pre-professional programs such as Pre-Med and Pre-Law)."
const INTRO_REVERSE =
  "Once a student engages with the campaign and completes the portal form, they become an enhanced responder. The information submitted via the form will then be automatically applied to the student's record. The data mapping below indicates how the area of study value will map to the associated Slate prompt {{academic-interest}}."

/**
 * The sheet's three header rows, then the lists in columns A–B, D–E and the
 * client's segments in F. An unmapped row leaves its other cell blank. Column
 * C is the sheet's spacer. A UTF-8 BOM so Excel does not mangle non-ASCII
 * slate names.
 */
export function translationsCsv(forward: ForwardRow[], reverse: ReverseRow[], segments: Segment[]): string {
  const header = [
    ['Responder Mapping', '', '', 'Guide to update the source format for program mapping', '', ''],
    [
      'Program Display Value {{wb-program}}',
      'Slate Prompt (Mapping to) {{academic-interest}}',
      '',
      'Slate Prompt {{academic-interest}}',
      'Mapping to {{wb-program}}',
      '',
    ],
    [INTRO_FORWARD, INTRO_REVERSE, '', '', '', 'Segments'],
  ]
  const length = Math.max(forward.length, reverse.length, segments.length)
  const body = Array.from({ length }, (_, i) => [
    forward[i]?.wbProgram ?? '',
    forward[i]?.slateName ?? '',
    '',
    reverse[i]?.slateName ?? '',
    reverse[i]?.wbProgram ?? '',
    segments[i]?.name ?? '',
  ])
  return '﻿' + toCsvGrid([...header, ...body])
}
